import { Component, OnInit } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ToastModule } from 'primeng/toast';
import { CommonModule } from '@angular/common';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators, FormsModule } from '@angular/forms';
import { FileUploadModule } from 'primeng/fileupload';
import { RadioButtonModule } from 'primeng/radiobutton';
import { ProgressBarModule } from 'primeng/progressbar';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TextareaModule } from 'primeng/textarea';
import { Editor } from 'primeng/editor';
import { MessageService } from 'primeng/api';
import { Router, ActivatedRoute } from '@angular/router';
import { ProductService } from '../../../core/services/product.service';
import { CustomerService } from '../../../core/services/customer.service';
import { ContractService } from '../../../core/services/contract.service';
import { FileService } from '../../../core/services/file';
import { SubscriberService } from '../../../core/services/subscriber.service';
import { ReservationService } from '../../../core/services/reservation.service';
import { EncryptionService } from '../../../core/services/encryption';
import { SETTINGS } from '../../../core/config/common.settings';
import { CreateContractRequest, UpdateContractRequest, Contract } from '../../../core/models/contract.model';
import { AuthService } from '../../../core/services/auth';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';

@Component({
  selector: 'app-add-edit-contract',
  imports: [ToastModule, CommonModule, CardModule, InputTextModule, InputNumberModule, SelectModule, ButtonModule, ReactiveFormsModule, FormsModule, FileUploadModule, RadioButtonModule, ProgressBarModule, ProgressSpinnerModule, TextareaModule, Editor, ConfirmDialogModule],
  templateUrl: './add-edit-contract.html',
  styleUrl: './add-edit-contract.scss',
  standalone: true,
  providers: [MessageService, ConfirmationService]
})
export class AddEditContract implements OnInit {
  contractForm: FormGroup;

  customerOptions: any[] = [];
  productsOptions: any[] = [];
  reservationId: string | null = null; // From URL parameter
  selectedProduct: any = null;
  reservationData: any = null; // Loaded reservation data
  isReservationLoaded: boolean = false;
  
  // Mode detection
  isEditMode: boolean = false;
  contractId: string | null = null;
  currentContract: Contract | null = null;
  
  // Field editability in update mode
  isUpdateMode: boolean = false;
  selectedProducts: any[] = [];
  productValues: { [key: string]: number } = {}; // Map of productId -> value
  contractValue: number = 0;
  duration: any = null;
  contractDurationsOptions: any[] = [];

  // File upload properties
  uploadedFiles: File[] = [];
  uploadedFileUrls: string[] = [];
  isFileUploading: boolean = false;
  dragOver: boolean = false;
  
  // Document tracking for update mode
  existingDocuments: string[] = []; // Documents that were already in the contract
  newDocuments: File[] = []; // Newly uploaded documents
  newDocumentUrls: string[] = []; // URLs for new documents
  
  // Subscriber document URLs for identification
  subscriberTermsConditionsUrl: string = '';
  subscriberPrivacyPolicyUrl: string = '';

  // Contract creation properties
  isSubmitting: boolean = false;
  isLoadingContract: boolean = false;
  isFormLoading: boolean = true;

  // User context
  currentUser: any = null;

  constructor(
    private fb: FormBuilder,
    private messageService: MessageService,
    private router: Router,
    private route: ActivatedRoute,
    private productService: ProductService, 
    private customerService: CustomerService, 
    private contractService: ContractService,
    private fileService: FileService,
    private subscriberService: SubscriberService,
    private reservationService: ReservationService,
    private encryption: EncryptionService,
    private authService: AuthService,
    private confirmationService: ConfirmationService,
  ) {
    this.contractForm = this.fb.group({
      customerId: ['', Validators.required],
      products: ['', Validators.required],
      contractValue: ['', Validators.required],
      duration: [''], // Will be set as required only in create mode
      endDate: [''],
      startDate: ['', Validators.required],
      description: [''],
      documents: [[]]
    });
  }

  ngOnInit(): void {
    // Initialize current user
    this.currentUser = this.authService.getLoggedInUser();
    
    // Get reservationId from URL parameter (route param) or query parameter
    this.reservationId = this.route.snapshot.paramMap.get('reservationId') 
      || this.route.snapshot.queryParams['reservationId'];
    
    // Check if we're in edit mode (contract ID in URL)
    this.contractId = this.route.snapshot.paramMap.get('id');
    this.isEditMode = !!this.contractId;
    
    // Check if we're in update mode (URL contains 'update')
    this.isUpdateMode = this.router.url.includes('/update/');
    
    if (!this.isEditMode) {
      this.contractForm.get('duration')?.setValidators([Validators.required]);
    } else {
      this.contractForm.get('duration')?.clearValidators();
      this.contractForm.get('duration')?.updateValueAndValidity();
    }
    
    this.loadProductsAndCustomers();
    this.loadContractDurations();
    
    // Load reservation data if creating a contract from reservation
    if (!this.isEditMode && this.reservationId) {
      this.loadReservationData();
    }
    
    // Handle customerId query parameter (for creating new contract with pre-filled customer)
    // Only if we don't have a reservationId (reservation takes priority)
    if (!this.isEditMode && !this.reservationId) {
      this.handleCustomerIdQueryParam();
    }
    
    if (this.isEditMode && this.contractId) {
      this.loadContractForEdit();
    }
  }

  private handleCustomerIdQueryParam(): void {
    this.route.queryParams.subscribe(params => {
      if (params['customerId']) {
        // Wait for customer options to load, then set the customer
        const checkAndSetCustomer = () => {
          if (this.customerOptions.length > 0) {
            const customerId = params['customerId'];
            const customerExists = this.customerOptions.some(opt => opt.value === customerId);
            if (customerExists) {
              this.contractForm.patchValue({
                customerId: customerId
              });
              // Disable the customer field to indicate it's pre-filled
              this.contractForm.get('customerId')?.disable();
              this.isReservationLoaded = true; // Reuse this flag to indicate pre-filled state
              
              this.messageService.add({
                severity: 'info',
                summary: 'Customer Pre-selected',
                detail: 'Customer has been pre-selected. You can still change it if needed.'
              });
            }
          } else {
            // If options not loaded yet, wait a bit and try again
            setTimeout(checkAndSetCustomer, 100);
          }
        };
        checkAndSetCustomer();
      }
    });
  }

  // Check if current user is FSR
  isFSR(): boolean {
    const roleIdentification = this.currentUser?.roleId?.identification || this.currentUser?.role?.identification;
    return roleIdentification === 'FSR';
  }

  // Check if contract is rejected
  isContractRejected(): boolean {
    return this.currentContract?.status === 'REJECTED';
  }

  // Check if contract was rejected and is now in draft state (deprecated - using isContractRejected instead)
  isRejectedAndInDraft(): boolean {
    return !!(
      this.currentContract?.rejectionHistory &&
      this.currentContract.rejectionHistory.length > 0 &&
      this.currentContract.status === 'DRAFT'
    );
  }

  private loadProductsAndCustomers(): void {
    let productsLoaded = false;
    let customersLoaded = false;

    const checkIfAllLoaded = () => {
      if (productsLoaded && customersLoaded && (!this.isEditMode || this.currentContract)) {
        this.isFormLoading = false;
      }
    };

    this.productService.getAllProducts().subscribe((products) => {
      this.productsOptions = products.map((product) => ({ label: product.name, value: product._id }));
      productsLoaded = true;
      checkIfAllLoaded();
    });

    this.customerService.getAllCustomers().subscribe((response: any) => {
      this.customerOptions = response.customers.map((customer: any) => ({ label: customer.businessName, value: customer._id }));
      customersLoaded = true;
      
      // If we have contract data waiting to be patched, do it now
      if (this.currentContract && this.isEditMode) {
        this.patchFormWithContractData();
      }
      
      checkIfAllLoaded();
    });
  }

  private loadReservationData(): void {
    if (!this.reservationId) return;

    this.reservationService.getReservationById(this.reservationId).subscribe({
      next: (response: any) => {
        this.reservationData = response.data; // API returns data under 'data' property
        this.isReservationLoaded = true;
        
        // Pre-populate form with reservation data
        this.populateFormFromReservation();
        
        this.messageService.add({
          severity: 'info',
          summary: 'Reservation Loaded',
          detail: 'Customer and products have been pre-selected from the reservation'
        });
      },
      error: (error) => {
        console.error('Error loading reservation data:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load reservation data. Please try again.'
        });
      }
    });
  }

  private populateFormFromReservation(): void {
    if (!this.reservationData) return;

    this.contractForm.patchValue({
      customerId: this.reservationData.customerId._id
    });

    this.selectedProducts = this.reservationData.products.map((product: any) => ({
      label: product.name,
      value: product._id
    }));

    // Initialize product values for reservation products
    this.selectedProducts.forEach((product: any) => {
      this.productValues[product.value] = 0;
    });

    this.updateProductsFormControl();
  }

  hasSelectedProducts(): boolean {
    return this.reservationData && this.reservationData.products && this.reservationData.products.length > 0;
  }

  private loadContractDurations(): void {
    const encryptedUserStr = localStorage.getItem(SETTINGS.LOGGED_IN_USER);
    
    if (encryptedUserStr) {
      const userStr = this.encryption.decrypt(encryptedUserStr);
      const user = JSON.parse(userStr);
      
      const subscriberId = user.subscriberId?._id;
      
      if (subscriberId) {
        this.subscriberService.findSubscriber(subscriberId).then((subscriber: any) => {
          // Check if subscriber is wrapped in a response object
          let actualSubscriber = subscriber;
          if (subscriber && subscriber.subscriber) {
            actualSubscriber = subscriber.subscriber;
          }
          
          // Store terms and conditions and privacy policy URLs for document identification
          if (actualSubscriber) {
            this.subscriberTermsConditionsUrl = actualSubscriber.termsConditions || '';
            this.subscriberPrivacyPolicyUrl = actualSubscriber.privacyPolicy || '';
          }
          
          if (actualSubscriber && actualSubscriber.contractDurationsList) {
            this.contractDurationsOptions = actualSubscriber.contractDurationsList.map((duration: string) => ({
              label: duration,
              value: duration
            }));
          } else {
            // Fallback to default options if no contract durations are configured
            this.contractDurationsOptions = [
              { label: '6 months', value: '6 months' },
              { label: '12 months', value: '12 months' }
            ];
          }
        }).catch((error: any) => {
          console.error('Error loading contract durations:', error);
          // Fallback to default options if subscriber data fails to load
          this.contractDurationsOptions = [
            { label: '6 months', value: '6 months' },
            { label: '12 months', value: '12 months' }
          ];
        });
      } else {
        // Fallback to default options if no subscriber ID
        this.contractDurationsOptions = [
          { label: '6 months', value: '6 months' },
          { label: '12 months', value: '12 months' }
        ];
      }
    } else {
      // Fallback to default options if no user data
      this.contractDurationsOptions = [
        { label: '6 months', value: '6 months' },
        { label: '12 months', value: '12 months' }
      ];
    }
  }

  private loadContractForEdit(): void {
    if (!this.contractId) return;
    
    this.isLoadingContract = true;
    this.contractService.getContractById(this.contractId).subscribe({
      next: (response: any) => {
        this.currentContract = response.data;
        
        // Prevent editing terminated contracts
        if (this.currentContract?.status?.toUpperCase() === 'TERMINATED') {
          this.messageService.add({
            severity: 'warn',
            summary: 'Cannot Edit',
            detail: 'Terminated contracts cannot be updated. Please create a new contract instead.'
          });
          this.router.navigate(['/admin/contract-management']);
          this.isLoadingContract = false;
          this.isFormLoading = false;
          return;
        }
        
        if (this.customerOptions.length > 0) {
          this.patchFormWithContractData();
        } else {
          this.waitForCustomerOptionsAndPatch();
        }
        
        this.isLoadingContract = false;
        this.isFormLoading = false;
      },
      error: (error) => {
        console.error('Error loading contract:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load contract data'
        });
        this.isLoadingContract = false;
        this.router.navigate(['/admin/contract-management']);
      }
    });
  }

  private waitForCustomerOptionsAndPatch(): void {
    const checkInterval = setInterval(() => {
      if (this.customerOptions.length > 0) {
        clearInterval(checkInterval);
        this.patchFormWithContractData();
      }
    }, 100);

    setTimeout(() => {
      clearInterval(checkInterval);
      if (this.customerOptions.length > 0) {
        this.patchFormWithContractData();
      }
    }, 5000);
  }

  private patchFormWithContractData(): void {
    if (!this.currentContract) return;

    // Extract customer ID - handle both string ID and object with _id
    let customerId: any = this.currentContract!.customerId;
    if (typeof customerId === 'object' && customerId && customerId._id) {
      customerId = customerId._id;
    }

    // Patch form with contract data
    this.contractForm.patchValue({
      customerId: customerId,
      contractValue: this.currentContract!.contractValue,
      startDate: this.formatDateForInput(this.currentContract!.startDate),
      endDate: this.formatDateForInput(this.currentContract!.endDate),
      description: this.currentContract!.description,
      documents: this.currentContract!.documents || []
    });
    
    // Force form validation
    this.contractForm.get('customerId')?.updateValueAndValidity();

    // Disable form controls in edit mode
    if (this.isEditMode) {
      this.contractForm.get('customerId')?.disable();
      this.contractForm.get('products')?.disable();
      
      // Allow edits on contractValue, startDate, and endDate if contract status is REJECTED
      if (this.isContractRejected()) {
        // Enable these fields for rejected contracts
        this.contractForm.get('contractValue')?.enable();
        this.contractForm.get('startDate')?.enable();
        
        // Add validators to endDate when it becomes editable
        this.contractForm.get('endDate')?.setValidators([Validators.required]);
        this.contractForm.get('endDate')?.enable();
        this.contractForm.get('endDate')?.updateValueAndValidity();
      } else {
        // Disable these fields in normal edit mode
        this.contractForm.get('contractValue')?.disable();
        this.contractForm.get('startDate')?.disable();
        this.contractForm.get('endDate')?.clearValidators();
        this.contractForm.get('endDate')?.disable();
        this.contractForm.get('endDate')?.updateValueAndValidity();
      }
    }

    // Set selected products
    if (this.currentContract!.products && Array.isArray(this.currentContract!.products)) {
      this.selectedProducts = this.currentContract!.products.map((product: any) => ({
        label: product.name || product,
        value: product._id || product
      }));
      
      // Load product values if available
      if (this.currentContract!.productValues) {
        // Handle both Map and object formats
        if (this.currentContract!.productValues instanceof Map) {
          this.currentContract!.productValues.forEach((value: number, key: string) => {
            this.productValues[key] = value;
          });
        } else {
          this.productValues = { ...this.currentContract!.productValues };
        }
      }
      
      this.updateProductsFormControl();
      this.calculateContractValue();
    }

    // Handle existing documents
    if (this.currentContract!.documents && Array.isArray(this.currentContract!.documents)) {
      // Store existing documents separately
      this.existingDocuments = [...this.currentContract!.documents];
      
      // Clear new document arrays
      this.newDocuments = [];
      this.newDocumentUrls = [];
      
      // For backward compatibility, also populate uploadedFiles for display
      this.uploadedFiles = [];
      this.uploadedFileUrls = [];
      
      // Create mock File objects for display purposes
      this.currentContract!.documents.forEach((docName: string) => {
        // Create a mock File object for display
        const mockFile = new File([], docName, { type: 'application/octet-stream' });
        this.uploadedFiles.push(mockFile);
        
        // Create a placeholder URL for display
        const placeholderUrl = `data:text/plain;base64,${btoa('Document: ' + docName)}`;
        this.uploadedFileUrls.push(placeholderUrl);
      });
    }
  }

  formatDateTime(date: string | Date): string {
    if (!date) return 'N/A';
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  }

  private formatDateForInput(date: string | Date): string {
    if (!date) return '';
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toISOString().split('T')[0];
  }



  // Handle product selection change
  onProductChange(event: any) {
    this.selectedProduct = event.value;
  }

  // Handle Add button click for products
  addSelectedProduct() {
    // Prevent adding products when creating from reservation
    if (this.isReservationLoaded) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Cannot Add Products',
        detail: 'Products are pre-selected from the reservation. You can only remove products, not add new ones.'
      });
      return;
    }

    if (this.selectedProduct) {
      console.log('Selected product:', this.selectedProduct);
      console.log('Current selected products:', this.selectedProducts);

      // Check if product already exists by comparing IDs (using 'value' field)
      const productExists = this.selectedProducts.some(p => p.value === this.selectedProduct.value);
      console.log('Product exists check:', productExists);

      if (!productExists) {
        const productToAdd = this.selectedProduct;
        this.selectedProducts.push(productToAdd);
        this.selectedProduct = null; // Reset selection
        console.log('Product added successfully. Updated array:', this.selectedProducts);
        
        // Initialize product value
        this.productValues[productToAdd.value] = 0;
        
        // Update form with selected products
        this.updateProductsFormControl();
        
        this.messageService.add({
          severity: 'success',
          summary: 'Product added',
          detail: `${productToAdd.label} has been added to the reservation`
        });
      } else {
        this.messageService.add({
          severity: 'warn',
          summary: 'Product exists',
          detail: 'This product is already in the reservation'
        });
        this.selectedProduct = null; // Reset selection
      }
    }
  }

  // Remove product from the list
  removeProduct(product: any) {
    const index = this.selectedProducts.findIndex(p => p.value === product.value);
    if (index > -1) {
      this.selectedProducts.splice(index, 1);
      
      // Remove product value from the map
      if (this.productValues[product.value]) {
        delete this.productValues[product.value];
      }
      
      // Recalculate contract value
      this.calculateContractValue();
      
      // Update form with selected products
      this.updateProductsFormControl();
      
      this.messageService.add({
        severity: 'info',
        summary: 'Product removed',
        detail: 'Product has been removed from the reservation'
      });
    }
  }

  // Update the products form control with selected products
  private updateProductsFormControl(): void {
    const productIds = this.selectedProducts.map(p => p.value);
    this.contractForm.patchValue({ products: productIds });
    console.log('Updated form products:', productIds);
    
    // Initialize product values for new products
    productIds.forEach((productId: string) => {
      if (!this.productValues[productId]) {
        this.productValues[productId] = 0;
      }
    });
    
    // Remove values for products that are no longer selected
    Object.keys(this.productValues).forEach((productId: string) => {
      if (!productIds.includes(productId)) {
        delete this.productValues[productId];
      }
    });
  }

  // Handle product value change
  onProductValueChange(productId: string, value: number): void {
    this.productValues[productId] = value || 0;
    this.calculateContractValue();
  }

  // Calculate total contract value from product values
  private calculateContractValue(): void {
    const total = Object.values(this.productValues).reduce((sum, value) => sum + (value || 0), 0);
    this.contractValue = total;
    this.contractForm.patchValue({ contractValue: total });
  }

  // Check if multiple products are selected
  hasMultipleProducts(): boolean {
    return this.selectedProducts.length > 1;
  }

  // Get product value for a specific product
  getProductValue(productId: string): number {
    return this.productValues[productId] || 0;
  }



  // File upload methods
  onFileUpload(event: any): void {
    const files = event.target.files;
    if (files && files.length > 0) {
      this.handleMultipleFileUpload(Array.from(files));
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.dragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.dragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragOver = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleMultipleFileUpload(Array.from(files));
    }
  }

  triggerFileInput(): void {
    const fileInput = document.getElementById('file-input-schedule') as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  private handleMultipleFileUpload(files: File[]): void {
    // Use centralized validation
    const validation = this.fileService.validateFiles(files, 'contract');
    if (!validation.isValid) {
      this.messageService.add({
        severity: 'error',
        summary: 'File validation failed',
        detail: validation.errors.join(', ')
      });
      return;
    }

    this.isFileUploading = true;

    // Upload files to AWS S3
    this.uploadFilesToS3(files);
  }

  private uploadFilesToS3(files: File[]): void {
    const uploadPromises = files.map(file => {
      return firstValueFrom(this.fileService.postFile(file, 'contract'));
    });

    Promise.all(uploadPromises)
      .then((responses: any[]) => {
        responses.forEach((response, index) => {
          if (response && response.url) {
            const file = files[index];
            if (this.isUpdateMode) {
              // In update mode, add to new documents
              const fileExists = this.newDocuments.some(existingFile => 
                existingFile.name === file.name && existingFile.size === file.size
              );
              
              if (!fileExists) {
                this.newDocuments.push(file);
                this.newDocumentUrls.push(response.url);
              }
            } else {
              // In create mode, add to regular uploaded files
              const fileExists = this.uploadedFiles.some(existingFile => 
                existingFile.name === file.name && existingFile.size === file.size
              );
              
              if (!fileExists) {
                this.uploadedFiles.push(file);
                this.uploadedFileUrls.push(response.url);
              }
            }
          }
        });

        this.isFileUploading = false;

        // Update form control with all document URLs
        this.updateDocumentsFormControl();

        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: `${files.length} file(s) uploaded successfully!`
        });
      })
      .catch(error => {
        console.error('File upload error:', error);
        this.isFileUploading = false;
        
        // Get more detailed error message
        let errorMessage = 'Failed to upload files. Please try again.';
        if (error.error?.message) {
          errorMessage = error.error.message;
        } else if (error.message) {
          errorMessage = error.message;
        } else if (typeof error === 'string') {
          errorMessage = error;
        }
        
        this.messageService.add({
          severity: 'error',
          summary: 'Upload Error',
          detail: errorMessage
        });
      });
  }

  // Update documents form control with all documents
  private updateDocumentsFormControl(): void {
    if (this.isUpdateMode) {
      // In update mode, combine existing and new documents
      const allDocuments = [...this.existingDocuments, ...this.newDocumentUrls];
      this.contractForm.patchValue({ documents: allDocuments });
    } else {
      // In create mode, use uploaded file URLs
      const fileUrls = this.uploadedFileUrls;
      this.contractForm.patchValue({ documents: fileUrls });
    }
  }

  removeUploadedFile(index: number): void {
    if (this.isUpdateMode) {
      // In update mode, only allow removing new documents
      const existingCount = this.existingDocuments.length;
      if (index < existingCount) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Cannot Remove',
          detail: 'Existing documents cannot be removed in update mode'
        });
        return;
      }
      
      // Remove from new documents
      const newIndex = index - existingCount;
      if (newIndex >= 0 && newIndex < this.newDocuments.length) {
        const url = this.newDocumentUrls[newIndex];
        if (url && !url.startsWith('data:')) {
          URL.revokeObjectURL(url);
        }
        
        this.newDocuments.splice(newIndex, 1);
        this.newDocumentUrls.splice(newIndex, 1);
        
        this.updateDocumentsFormControl();
        
        this.messageService.add({
          severity: 'info',
          summary: 'File Removed',
          detail: 'New file has been removed from the upload list'
        });
      }
    } else {
      // In create mode, allow removing any file
      if (index >= 0 && index < this.uploadedFiles.length) {
        const url = this.uploadedFileUrls[index];
        if (url && !url.startsWith('data:')) {
          URL.revokeObjectURL(url);
        }
        
        this.uploadedFiles.splice(index, 1);
        this.uploadedFileUrls.splice(index, 1);
        
        this.updateDocumentsFormControl();
        
        this.messageService.add({
          severity: 'info',
          summary: 'File Removed',
          detail: 'File has been removed from the upload list'
        });
      }
    }
  }

  removeAllUploadedFiles(): void {
    if (this.isUpdateMode) {
      // In update mode, only remove new documents
      this.newDocumentUrls.forEach(url => {
        if (url && !url.startsWith('data:')) {
          URL.revokeObjectURL(url);
        }
      });
      
      this.newDocuments = [];
      this.newDocumentUrls = [];
      this.updateDocumentsFormControl();
      
      this.messageService.add({
        severity: 'info',
        summary: 'New Files Removed',
        detail: 'All new files have been removed from the upload list'
      });
    } else {
      // In create mode, remove all files
      this.uploadedFileUrls.forEach(url => {
        if (url && !url.startsWith('data:')) {
          URL.revokeObjectURL(url);
        }
      });
      
      this.uploadedFiles = [];
      this.uploadedFileUrls = [];
      this.contractForm.patchValue({ documents: [] });
      
      this.messageService.add({
        severity: 'info',
        summary: 'Files Removed',
        detail: 'All files have been removed from the upload list'
      });
    }
  }

  isImageFile(file: File): boolean {
    return file.type.startsWith('image/');
  }

  // Get display name for a document URL
  private getDocumentDisplayName(docUrl: string): string | null {
    if (this.subscriberTermsConditionsUrl && docUrl === this.subscriberTermsConditionsUrl) {
      return 'Terms and Conditions';
    }
    if (this.subscriberPrivacyPolicyUrl && docUrl === this.subscriberPrivacyPolicyUrl) {
      return 'Privacy Policy';
    }
    return null; // Return null if not a subscriber document
  }

  // Get all documents for display (existing + new)
  getAllDocumentsForDisplay(): any[] {
    if (this.isUpdateMode) {
      const allDocs: any[] = [];
      
      // Add existing documents
      this.existingDocuments.forEach((docUrl, index) => {
        const displayName = this.getDocumentDisplayName(docUrl);
        allDocs.push({
          name: displayName || docUrl, // Use display name if available, otherwise use URL
          originalUrl: docUrl,
          isExisting: true,
          index: index,
          isSubscriberDocument: !!displayName
        });
      });
      
      // Add new documents
      this.newDocuments.forEach((file, index) => {
        const docUrl = this.newDocumentUrls[index];
        const displayName = docUrl ? this.getDocumentDisplayName(docUrl) : null;
        allDocs.push({
          name: displayName || file.name,
          file: file,
          originalUrl: docUrl,
          isExisting: false,
          index: this.existingDocuments.length + index,
          isSubscriberDocument: !!displayName
        });
      });
      
      return allDocs;
    } else {
      // In create mode, return uploaded files
      return this.uploadedFiles.map((file, index) => {
        const docUrl = this.uploadedFileUrls[index];
        const displayName = docUrl ? this.getDocumentDisplayName(docUrl) : null;
        return {
          name: displayName || file.name,
          file: file,
          originalUrl: docUrl,
          isExisting: false,
          index: index,
          isSubscriberDocument: !!displayName
        };
      });
    }
  }

  // Check if a document is existing (cannot be removed)
  isExistingDocument(index: number): boolean {
    if (this.isUpdateMode) {
      return index < this.existingDocuments.length;
    }
    return false;
  }

  getEditorConfig() {
    return {
      height: '200px',
      style: {
        'height': '200px',
        'font-size': '14px'
      },
      formats: ['bold', 'italic', 'underline', 'list', 'bullet', 'link', 'header', 'font', 'size'],
      modules: {
        toolbar: {
          container: [
            ['bold', 'italic', 'underline'],
            [{ 'list': 'ordered'}],
            ['link']
          ]
        }
      }
    };
  }


  // Contract creation/update methods
  onSubmit(): void {
    if (this.isUpdateMode) {
      // If contract status is REJECTED, also validate editable fields
      if (this.isContractRejected()) {
        // Validate product values if multiple products are selected
        if (this.hasMultipleProducts()) {
          const missingValues = this.selectedProducts.filter(p => !this.productValues[p.value] || this.productValues[p.value] <= 0);
          if (missingValues.length > 0) {
            this.messageService.add({
              severity: 'warn',
              summary: 'Validation Error',
              detail: 'Please enter a value for all selected products.'
            });
            return;
          }
        }
        
        if (!this.contractForm.value.contractValue || this.contractForm.value.contractValue <= 0) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Validation Error',
            detail: 'Please enter a valid contract value.'
          });
          return;
        }
        
        if (!this.contractForm.value.startDate) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Validation Error',
            detail: 'Please enter a start date.'
          });
          return;
        }
        
        if (!this.contractForm.value.endDate) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Validation Error',
            detail: 'Please enter an end date.'
          });
          return;
        }
        
        // Validate that end date is after start date
        const startDate = new Date(this.contractForm.value.startDate);
        const endDate = new Date(this.contractForm.value.endDate);
        if (endDate <= startDate) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Validation Error',
            detail: 'End date must be after start date.'
          });
          return;
        }
      }
      
      this.updateContract();
    } else {
      // In create mode, validate all fields
      this.updateProductsFormControl();
      
      // Custom validation for duration in create mode
      if (!this.contractForm.value.duration) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Validation Error',
          detail: 'Please select a duration.'
        });
        return;
      }
      
      if (this.contractForm.valid) {
        this.createContract();
      } else {
        console.log('Form validation errors:', this.contractForm.errors);
        console.log('Form status:', this.contractForm.status);
        console.log('Form value:', this.contractForm.value);
        
        this.messageService.add({
          severity: 'warn',
          summary: 'Validation Error',
          detail: 'Please fill in all required fields and select at least one product.'
        });
      }
    }
  }

  resubmitContract(): void {
    this.confirmationService.confirm({
      message: 'Are you sure you want to resubmit this contract for approval? The contract status will change to DRAFT and it will be sent for review.',
      header: 'Resubmit Contract',
      icon: 'pi pi-exclamation-triangle',
      rejectButtonStyleClass: 'p-button-outlined p-button-secondary',
      accept: () => {
        this.updateContract(true);
      }
    });
  }

  private updateContract(resubmit: boolean = false): void {
    if (!this.contractId) return;
    
    // Prevent updating terminated contracts
    if (this.currentContract?.status?.toUpperCase() === 'TERMINATED') {
      this.messageService.add({
        severity: 'warn',
        summary: 'Cannot Update',
        detail: 'Terminated contracts cannot be updated. Please create a new contract instead.'
      });
      return;
    }
    
    this.isSubmitting = true;

    // Store customer ID before API call (use getRawValue for disabled fields)
    const customerId = this.contractForm.get('customerId')?.value || this.contractForm.getRawValue().customerId;
    console.log('Customer ID for update:', customerId);

    // In update mode, send description and documents
    // If contract status is REJECTED, also send contractValue, startDate, and endDate
    const updateData: UpdateContractRequest = {
      description: this.contractForm.value.description,
      documents: this.contractForm.value.documents || []
    };

    // If contract status is REJECTED, include editable fields
    if (this.isContractRejected()) {
      updateData.contractValue = this.contractForm.value.contractValue || this.contractForm.getRawValue().contractValue;
      updateData.startDate = this.contractForm.value.startDate;
      updateData.endDate = this.contractForm.value.endDate;
      
      // Include product values if multiple products are selected
      if (this.hasMultipleProducts()) {
        updateData.productValues = this.productValues;
      }
    }

    // If resubmitting, change status to DRAFT
    if (resubmit) {
      updateData.status = 'DRAFT';
    }

    this.contractService.updateContract(this.contractId, updateData).subscribe({
      next: (response) => {
        this.isSubmitting = false;
        const message = resubmit 
          ? 'Contract resubmitted successfully! It is now pending approval.'
          : 'Contract updated successfully!';
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: message
        });
        
        // Navigate to customer single page - contracts tab
        setTimeout(() => {
          if (customerId) {
            this.router.navigate(['/admin/view-customer', customerId], { queryParams: { tab: 'contracts' } }).then(() => {
              window.scrollTo(0, 0);
            });
          } else {
            this.router.navigate(['/admin/contract-management']).then(() => {
              window.scrollTo(0, 0);
            });
          }
        }, 2000);
      },
      error: (error) => {
        this.isSubmitting = false;
        console.error('Error updating contract:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error.error?.message || 'Failed to update contract. Please try again.'
        });
      }
    });
  }

  private createContract(): void {
    this.isSubmitting = true;

    // Validate product values if multiple products are selected
    if (this.hasMultipleProducts()) {
      const missingValues = this.selectedProducts.filter(p => !this.productValues[p.value] || this.productValues[p.value] <= 0);
      if (missingValues.length > 0) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Validation Error',
          detail: 'Please enter a value for all selected products.'
        });
        this.isSubmitting = false;
        return;
      }
    }

    const contractData: CreateContractRequest = {
      customerId: this.contractForm.value.customerId,
      reservationId: this.reservationId || '', // Get from URL parameter
      products: this.contractForm.value.products,
      productValues: this.hasMultipleProducts() ? this.productValues : undefined,
      contractValue: this.hasMultipleProducts() ? this.contractValue : (this.contractForm.value.contractValue || this.contractForm.getRawValue().contractValue),
      duration: this.contractForm.value.duration,
      startDate: this.contractForm.value.startDate,
      description: this.contractForm.value.description,
      documents: this.contractForm.value.documents || []
    };

    this.contractService.createContract(contractData).subscribe({
      next: (response) => {
        this.isSubmitting = false;
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Contract created successfully!'
        });
        
        // Store customer ID before resetting form (use getRawValue for disabled fields)
        const customerId = this.contractForm.get('customerId')?.value || this.contractForm.getRawValue().customerId;
        console.log('Customer ID for create:', customerId);
        
        // Reset form
        this.contractForm.reset();
        this.selectedProducts = [];
        this.selectedProduct = null;
        this.productValues = {};
        this.contractValue = 0;
        this.removeAllUploadedFiles();
        
        // Navigate to customer single page - contracts tab
        setTimeout(() => {
          if (customerId) {
            this.router.navigate(['/admin/view-customer', customerId], { queryParams: { tab: 'contracts' } }).then(() => {
              window.scrollTo(0, 0);
            });
          } else {
            this.router.navigate(['/admin/contract-management']).then(() => {
              window.scrollTo(0, 0);
            });
          }
        }, 2000);
      },
      error: (error) => {
        this.isSubmitting = false;
        console.error('Error creating contract:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error.error?.message || 'Failed to create contract. Please try again.'
        });
      }
    });
  }

}

